// @mui
import { useSettingsContext } from 'src/components/settings';
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, Snackbar, Alert, CircularProgress } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState } from 'react';
// components
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import useHeroSection from 'src/hooks/use-homepage-data';
import { useSnackbar } from 'notistack';
import { endpoints } from 'src/utils/axios';
import { postRequestForWebsite } from 'src/services';
import { handleErrorMessages } from 'src/services/fetching';

// ----------------------------------------------------------------------

export default function WorkFlowSection() {
  const settings = useSettingsContext();
  const homePageData = useSelector((state) => state.website.homePage);
  const { fetchOnMount } = useHeroSection();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    // Main Section
    sectionTitle: "",
    sectionTitleColor: "#333333",
    mainHeading: "",
    mainHeadingColor: "#8BC34A", // Green color from the image

    // Steps Configuration
    steps: [
      {
        stepNumber: "01",
        stepNumberColor: "#666666",
        title: ":",
        titleColor: "#8BC34A",
        description: "",
        descriptionColor: "#333333",
        iconBgColor: "#8BC34A",
        iconColor: ""
      },
      {
        stepNumber: "02",
        stepNumberColor: "#666666",
        title: "",
        titleColor: "#8BC34A",
        description: "",
        descriptionColor: "#333333",
        iconBgColor: "#8BC34A",
        iconColor: ""
      },
      {
        stepNumber: "03",
        stepNumberColor: "#666666",
        title: ":",
        titleColor: "#8BC34A",
        description: "",
        descriptionColor: "#333333",
        iconBgColor: "#8BC34A",
        iconColor: ""
      },
      {
        stepNumber: "04",
        stepNumberColor: "#666666",
        title: "",
        titleColor: "#8BC34A",
        description: "",
        descriptionColor: "#333333",
        iconBgColor: "#8BC34A",
        iconColor: "#FFFFFF"
      },
      {
        stepNumber: "05",
        stepNumberColor: "#666666",
        title: "",
        titleColor: "#8BC34A",
        description: "",
        descriptionColor: "#333333",
        iconBgColor: "#8BC34A",
        iconColor: "#FFFFFF"
      },
      {
        stepNumber: "06",
        stepNumberColor: "#666666",
        title: "",
        titleColor: "#8BC34A",
        description: "",
        descriptionColor: "#333333",
        iconBgColor: "#8BC34A",
        iconColor: "#FFFFFF"
      }
    ],

    // Visual Settings
    backgroundColor: "#FFFFFF",
    containerMaxWidth: "1200px",
    stepSpacing: "40px",
    connectorLineColor: "#E0E0E0",
    connectorLineStyle: "dotted", // solid, dashed, dotted
    connectorLineWidth: "2px"
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (key.includes('[') && key.includes(']')) {
          const arrayKey = key.split('[')[0];
          const index = parseInt(key.split('[')[1].split(']')[0]);
          current = current[arrayKey][index];
        } else {
          current = current[key];
        }
      }

      const finalKey = keys[keys.length - 1];
      current[finalKey] = value;
      return newData;
    });
  };

  const addStep = () => {
    const newStepNumber = String(formData.steps.length + 1).padStart(2, '0');
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, {
        stepNumber: newStepNumber,
        stepNumberColor: "#666666",
        title: "New Step:",
        titleColor: "#8BC34A",
        description: "Enter step description here",
        descriptionColor: "#333333",
        iconBgColor: "#8BC34A",
        iconColor: "#FFFFFF"
      }]
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((step, i) => ({
        ...step,
        stepNumber: String(i + 1).padStart(2, '0')
      }))
    }));
  };

  const updateStep = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };


  useEffect(() => {
    fetchOnMount();
  }, []);
  useEffect(() => {
    if (homePageData?.work_flow_section) {
      // Create a deep copy of the community section to avoid read-only property issues
      const workFlowSection = JSON.parse(JSON.stringify(homePageData.work_flow_section));
      setFormData(prev => ({
        ...prev,
        ...workFlowSection
      }));
    }
  }, [homePageData])


  // const handleSave = (isDraft = false, exportJson = false) => {
  //   if (exportJson) {
  //     // Export as JSON for developer use
  //     const dataStr = JSON.stringify(formData, null, 2);
  //     const dataBlob = new Blob([dataStr], { type: 'application/json' });
  //     const url = URL.createObjectURL(dataBlob);
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.download = 'workflow-section-config.json';
  //     link.click();
  //     return;
  //   }

  //   console.log(isDraft ? 'Saving as draft...' : 'Publishing...', formData);
  //   // You can implement the API call here
  // };


  const handleSave = async () => {

    setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.home_page.hero_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(homePageData)),
            work_flow_section: JSON.parse(JSON.stringify(formData))
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }

  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Work Flow Process Section
        </Typography>

        {/* Section Headers */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Section Headers</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Section Title</Typography>
                <TextField
                  fullWidth
                  value={formData.sectionTitle}
                  onChange={(e) => updateFormData('sectionTitle', e.target.value)}
                  placeholder="Enter section title"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Title Color</Typography>
                <TextField
                  type="color"
                  value={formData.sectionTitleColor}
                  onChange={(e) => updateFormData('sectionTitleColor', e.target.value)}
                  sx={{ width: '100%', '& input': { height: 40 } }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Main Heading (Step-by-Step Guide)</Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={formData.mainHeading}
              onChange={(e) => updateFormData('mainHeading', e.target.value)}
              placeholder="Enter main heading"
              helperText="This appears as the subtitle under the main section title"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Main Heading Color</Typography>
            <TextField
              type="color"
              value={formData.mainHeadingColor}
              onChange={(e) => updateFormData('mainHeadingColor', e.target.value)}
              sx={{ width: 200, '& input': { height: 40 } }}
            />
          </Box>
        </Box>



        {/* Steps Configuration */}
        <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Process Steps</Typography>
            </Stack>
            {/* <Button
              color="success"
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addStep}
            >
              Add Step
            </Button> */}
          </Stack>

          {formData.steps.map((step, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'background.neutral' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Typography variant="h6">Step {step.stepNumber}</Typography>
                {/* <Button
                  color="error"
                  variant="contained"
                  size="small"
                  startIcon={<Iconify icon="mingcute:close-line" />}
                  onClick={() => removeStep(index)}
                  disabled={formData.steps.length <= 1}
                >
                  Remove Step
                </Button> */}
              </Stack>

              <Grid container spacing={3}>


                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Step Title"
                    value={step.title}
                    onChange={(e) => updateStep(index, 'title', e.target.value)}
                    placeholder="Registration:"
                    helperText="Include colon if needed"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Title Color"
                    type="color"
                    value={step.titleColor}
                    onChange={(e) => updateStep(index, 'titleColor', e.target.value)}
                    sx={{ '& input': { height: 40 } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Step Description"
                    value={step.description}
                    onChange={(e) => updateStep(index, 'description', e.target.value)}
                    placeholder="Enter step description"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Description Color"
                    type="color"
                    value={step.descriptionColor}
                    onChange={(e) => updateStep(index, 'descriptionColor', e.target.value)}
                    sx={{ '& input': { height: 40 } }}
                  />
                </Grid>
                {/* <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Icon Background Color"
                    type="color"
                    value={step.iconBgColor}
                    onChange={(e) => updateStep(index, 'iconBgColor', e.target.value)}
                    sx={{ '& input': { height: 40 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Icon Color"
                    type="color"
                    value={step.iconColor}
                    onChange={(e) => updateStep(index, 'iconColor', e.target.value)}
                    sx={{ '& input': { height: 40 } }}
                  />
                </Grid> */}
              </Grid>
            </Paper>
          ))}
        </Box>



        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} >
          {/* <Button
            variant="outlined"
            color="inherit"
            onClick={() => handleSave(false, true)}
            startIcon={<Iconify icon="mingcute:download-line" />}
          >
            Export JSON
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => handleSave(true)}
          >
            Save as Draft
          </Button> */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="eva:save-fill" />}
            onClick={() => handleSave(false)}
          >
            Update
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
